import type { MobxDocument } from '../firestore-mobx'
import {
  empty,
  type FirestoreSlideQuestion,
} from '../firestore/SlideQuestion/schema'
import type { FirebaseRepository } from './FirebaseRepository'
import { computed, makeObservable } from 'mobx'
import { SlideQuestionType, slideQuestionType } from './SlideQuestionType'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'

// This should be called SlideDeckQuestion, but Dart has it as SlideQuestion,
// so for compatibility reasons we keep it as SlideQuestion
export class SlideQuestion extends ObservableModelWithDecorators<FirestoreSlideQuestion> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  static readonly soloBotQuestionTypes: readonly SlideQuestionType[] = [
    SlideQuestionType.moderatorBot,
    SlideQuestionType.tutorBot,
    SlideQuestionType.graderBot,
    SlideQuestionType.rolePlayBot,
    SlideQuestionType.formCompletionBot,
  ] as const

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlideQuestion>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get questionType(): SlideQuestionType | undefined {
    if (!this.hasData) return undefined
    return slideQuestionType(this.data.questionType)
  }

  @computed
  get worstPossibleScore() {
    // if not sorting, it's 0
    if (this.data.questionType !== 4) return 0

    // sorting
    const n = this.data.answers.length
    const halfN = Math.floor(n / 2)
    return 2 * (halfN * (halfN + 1) - halfN * (1 - (n % 2)))
  }

  get getWorstSortingScore() {
    // sorting
    const n = this.data.answers.length
    const halfN = Math.floor(n / 2)
    return 2 * (halfN * (halfN + 1) - halfN * (1 - (n % 2)))
  }

  @computed
  get hasSlideId() {
    return Boolean(this.data.slideId)
  }

  @computed
  get hasGroupSlideId() {
    return Boolean(this.data.groupSlideId)
  }

  @computed
  get isQuiz(): boolean {
    return (
      this.questionType === SlideQuestionType.multipleChoice ||
      this.questionType === SlideQuestionType.sorting
    )
  }

  @computed
  get isSorting(): boolean {
    return this.questionType === SlideQuestionType.sorting
  }

  @computed
  get questionWithoutNumbersAtBeginning(): string {
    const pattern = /^\d+\.\s/
    return this.data.question.replace(pattern, '')
  }

  /// test element is of type poll
  get isPoll(): boolean {
    return (
      this.questionType === SlideQuestionType.poll ||
      this.questionType === SlideQuestionType.customPoll
    )
  }

  get isPreMeetingQuestion(): boolean {
    return (
      this.data.slideId === PRE_MEETING_QUESTION_SLIDE_ID &&
      (this.data.questionType === SlideQuestionType.sorting ||
        this.data.questionType === SlideQuestionType.multipleChoice)
    )
  }

  /**
   * interactive element type of question is only relevant when
   * user is participating in a solo experience (no other participants)
   */
  @computed
  get isSoloBotQuestionType(): boolean {
    return SlideQuestion.soloBotQuestionTypes.includes(this.data.questionType)
  }

  /**
   * Can the current question be graded (does it have a correct answer)
   */
  get isGraded(): boolean {
    switch (this.data.questionType) {
      // sorting and multiple choice are graded (have correct answers)
      case SlideQuestionType.sorting:
      case SlideQuestionType.multipleChoice:
        return true
      // polls are answered but do not have correct answers
      case SlideQuestionType.customPoll:
      case SlideQuestionType.poll:
      case SlideQuestionType.title: // title is purely UI, not interacted with
      case SlideQuestionType.talkTrack: // purely for server, not interacted with
      case SlideQuestionType.moderatorBot:
      case SlideQuestionType.displayModerationTask:
      case SlideQuestionType.moderationTask:
      case SlideQuestionType.tutorBot:
      case SlideQuestionType.graderBot:
      case SlideQuestionType.rolePlayBot:
      case SlideQuestionType.formCompletionBot:
      case SlideQuestionType.interactiveFormElement:
      case SlideQuestionType.popUpInstructions:
      case SlideQuestionType.sortableStringList:
      case SlideQuestionType.aiAgent:
      case SlideQuestionType.suggestedTimer:
        return false
    }
  }

  get adminLabel(): string {
    switch (this.data.questionType) {
      case SlideQuestionType.displayModerationTask:
        return 'Display Moderation Tasks'
      case SlideQuestionType.moderatorBot:
        if (!this.data.question) {
          return 'Solo Moderator Bot'
        } else {
          return this.data.question
        }
      case SlideQuestionType.graderBot:
        if (!this.data.question) {
          return 'Solo Grader Bot'
        } else {
          return this.data.question
        }
      case SlideQuestionType.tutorBot:
        if (!this.data.question) {
          return 'Solo Tutor Bot'
        } else {
          return this.data.question
        }
      case SlideQuestionType.rolePlayBot:
        if (!this.data.question) {
          return 'Solo Role Play Bot'
        } else {
          return this.data.question
        }
      case SlideQuestionType.formCompletionBot:
        return this.data.question || 'Interactive Form Bot'
      default:
        return this.data.question
    }
  }

  @computed
  get slideId(): string | undefined {
    return this.data.slideId || undefined
  }
}
